// Shopify Custom App — two-path connect modal.
//
// Shopify's Custom Distribution model requires us (the Partner) to add
// each merchant's shop domain to the app's allowed-stores list in the
// Partner Dashboard BEFORE their OAuth install can succeed. There is no
// Partner API mutation for this — Shopify gates the allowed-stores list
// behind the Partner Dashboard UI by design.
//
// So the modal supports two paths:
//
//   (1) REQUEST ACCESS (new store, first time) — brand submits their
//       shop slug, backend creates a ShopifyAccessRequest + Slack-pings
//       the owner. Modal polls /access-request every 5s; status flips
//       pending → ready once the owner approves. From ready the brand
//       clicks Install → OAuth.
//
//   (2) INSTALL NOW (already approved) — if the owner has already added
//       this shop to the Partner Dashboard allowlist (via (1) or
//       manually), the direct OAuth path still works. Shown as a
//       secondary action for brands that already know they're approved.
//
// Modal fetches the brand's latest access-request on open so repeat
// opens see the current status (not a stale empty form).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Button, FormControl, FormErrorMessage, FormHelperText,
  FormLabel, HStack, Input, InputGroup, InputRightAddon, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Spinner, Text, VStack, useToast,
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';

// Matches the backend's SHOP_RE (services/shopifyAppOAuthService.js).
// The modal collects the slug ("mystore"); we build the full
// "mystore.myshopify.com" before validation + API call.
const SHOP_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

const POLL_INTERVAL_MS = 5000;

type RequestStatus = 'pending' | 'ready' | 'installed' | 'rejected';

type AccessRequest = {
  _id:         string;
  shopDomain:  string;
  status:      RequestStatus;
  createdAt:   string;
  updatedAt:   string;
  approvedAt:  string | null;
  rejectedAt:  string | null;
  installedAt: string | null;
  notes:       string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // true when the connect was kicked from the onboarding flow — threads
  // through the backend OAuth state so the callback bounces to
  // /onboarding/connect instead of /brand (keeps the operator in the
  // onboarding sequence).
  onboarding?: boolean;
  // 2026-10-16 — 'custom' (default) = legacy two-path flow (request
  // access → wait for owner approval → install). Any other value =
  // Public distribution direct install (no request, no polling, straight
  // to OAuth). Backend's /shopify/status endpoint publishes the current
  // SHOPIFY_APP_MODE which the caller forwards here.
  appMode?: 'custom' | 'public_staging' | 'public';
};

export function ShopifyConnectModal({ isOpen, onClose, onboarding = false, appMode = 'custom' }: Props) {
  const isPublicMode = appMode !== 'custom';
  const toast = useToast();
  const [slug, setSlug] = useState('');
  const [touched, setTouched] = useState(false);

  const [existing, setExisting] = useState<AccessRequest | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [installing, setInstalling] = useState(false);

  const pollTimer = useRef<number | null>(null);

  const trimmed = slug.trim().toLowerCase();
  const invalid = touched && (!trimmed || !SHOP_SLUG_RE.test(trimmed));

  // Load the latest request for this brand when the modal opens so we
  // pick up a pending/ready/rejected row from a previous session rather
  // than making the operator re-type their slug.
  //
  // Skipped in public mode — no access-request concept, direct install.
  useEffect(() => {
    if (!isOpen || isPublicMode) return;
    let cancelled = false;
    setLoadingExisting(true);
    apiJson<{ request: AccessRequest | null }>('/api/integrations/shopify/access-request')
      .then((r) => {
        if (cancelled) return;
        setExisting(r.request);
        if (r.request && (r.request.status === 'pending' || r.request.status === 'ready')) {
          // Pre-fill the slug input from the saved shopDomain.
          setSlug(r.request.shopDomain.replace(/\.myshopify\.com$/, ''));
        }
      })
      .catch(() => { /* non-fatal — modal still works for first-time submit */ })
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Poll while there's a pending request so the UI flips to "ready"
  // without a page refresh when the owner approves in Slack / admin.
  // No-op in public mode (no access-request lifecycle to poll).
  useEffect(() => {
    if (!isOpen || isPublicMode) return;
    if (!existing || existing.status !== 'pending') {
      if (pollTimer.current != null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    pollTimer.current = window.setInterval(() => {
      apiJson<{ request: AccessRequest | null }>('/api/integrations/shopify/access-request')
        .then((r) => { if (r.request) setExisting(r.request); })
        .catch(() => { /* stay on current state */ });
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current != null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [isOpen, existing?.status, existing?._id]);

  const close = useCallback(() => {
    if (requesting || installing) return;
    setSlug('');
    setTouched(false);
    setExisting(null);
    onClose();
  }, [requesting, installing, onClose]);

  const requestAccess = useCallback(async () => {
    setTouched(true);
    if (!trimmed || !SHOP_SLUG_RE.test(trimmed)) return;
    setRequesting(true);
    try {
      const shopDomain = `${trimmed}.myshopify.com`;
      const res = await apiJson<{ requestId: string; status: RequestStatus; shopDomain: string; created: boolean }>(
        '/api/integrations/shopify/request-access',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shopDomain }),
        }
      );
      setExisting({
        _id:         res.requestId,
        shopDomain:  res.shopDomain,
        status:      res.status,
        createdAt:   new Date().toISOString(),
        updatedAt:   new Date().toISOString(),
        approvedAt:  null,
        rejectedAt:  null,
        installedAt: null,
        notes:       null,
      });
      toast({
        title: res.created ? 'Access request submitted' : 'You already have a pending request',
        description: res.status === 'ready'
          ? 'Your store is already approved — click Install now.'
          : 'We\'ll approve your store (usually within an hour). It\'s safe to close this window — you\'ll see an "Install" button on the Integrations page when it\'s ready.',
        status: 'success', duration: 5000,
      });
    } catch (err: unknown) {
      toast({
        title: 'Request failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error', duration: 5000,
      });
    } finally {
      setRequesting(false);
    }
  }, [trimmed, toast]);

  const installNow = useCallback(async () => {
    // Resolve the shop to use: prefer the existing request row's
    // shopDomain so a ready-state Install button doesn't rely on the
    // slug input being populated. Fall back to the input otherwise.
    let shop: string | null = null;
    if (existing && (existing.status === 'ready' || existing.status === 'installed')) {
      shop = existing.shopDomain;
    } else {
      setTouched(true);
      if (!trimmed || !SHOP_SLUG_RE.test(trimmed)) return;
      shop = `${trimmed}.myshopify.com`;
    }
    setInstalling(true);
    try {
      const res = await apiJson<{ authorizeUrl: string }>(
        '/api/integrations/shopify/connect',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop, onboarding }),
        }
      );
      window.location.assign(res.authorizeUrl);
    } catch (err: unknown) {
      toast({
        title: 'Shopify connect failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error', duration: 5000,
      });
      setInstalling(false);
    }
  }, [existing, trimmed, onboarding, toast]);

  const statusBanner = renderStatusBanner(existing);
  const showRequestForm = !existing || existing.status === 'rejected' || existing.status === 'installed';

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Connect Shopify</ModalHeader>
        <ModalCloseButton isDisabled={requesting || installing} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {isPublicMode ? (
              <>
                <Text fontSize="sm" color="brand.muted">
                  Enter your Shopify store slug and we'll take you to
                  Shopify to approve the connection. You'll be back
                  here once Shopify confirms.
                </Text>
                <FormControl isRequired isInvalid={invalid}>
                  <FormLabel>Store slug</FormLabel>
                  <InputGroup>
                    <Input
                      placeholder="mystore"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      onBlur={() => setTouched(true)}
                      isDisabled={installing}
                      autoFocus
                      spellCheck={false}
                      autoCapitalize="none"
                    />
                    <InputRightAddon>.myshopify.com</InputRightAddon>
                  </InputGroup>
                  {invalid ? (
                    <FormErrorMessage>
                      Store slug must be letters, numbers, or hyphens only.
                    </FormErrorMessage>
                  ) : (
                    <FormHelperText>
                      Found in your Shopify admin URL:
                      {' '}<Text as="code">admin.shopify.com/store/<Text as="b">your-slug</Text></Text>
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            ) : loadingExisting ? (
              <HStack><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Checking status…</Text></HStack>
            ) : (
              <>
                {statusBanner}

                {showRequestForm && (
                  <>
                    <Text fontSize="sm" color="brand.muted">
                      Shopify requires us to approve each merchant store individually.
                      Enter your store slug below and we'll notify our team — approval
                      usually takes under an hour. Once approved you'll see an{' '}
                      <Text as="b">Install</Text> button right here.
                    </Text>
                    <FormControl isRequired isInvalid={invalid}>
                      <FormLabel>Store slug</FormLabel>
                      <InputGroup>
                        <Input
                          placeholder="mystore"
                          value={slug}
                          onChange={(e) => setSlug(e.target.value)}
                          onBlur={() => setTouched(true)}
                          isDisabled={requesting || installing}
                          autoFocus
                          spellCheck={false}
                          autoCapitalize="none"
                        />
                        <InputRightAddon>.myshopify.com</InputRightAddon>
                      </InputGroup>
                      {invalid ? (
                        <FormErrorMessage>
                          Store slug must be letters, numbers, or hyphens only.
                        </FormErrorMessage>
                      ) : (
                        <FormHelperText>
                          Found in your Shopify admin URL:
                          {' '}<Text as="code">admin.shopify.com/store/<Text as="b">your-slug</Text></Text>
                        </FormHelperText>
                      )}
                    </FormControl>
                  </>
                )}
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={close} isDisabled={requesting || installing}>
            Cancel
          </Button>
          {isPublicMode ? (
            <Button
              colorScheme="purple"
              onClick={installNow}
              isLoading={installing}
              loadingText="Opening Shopify..."
            >
              Install
            </Button>
          ) : existing && existing.status === 'ready' ? (
            <Button
              colorScheme="purple"
              onClick={installNow}
              isLoading={installing}
              loadingText="Opening Shopify..."
            >
              Install on {existing.shopDomain}
            </Button>
          ) : showRequestForm ? (
            <Button
              colorScheme="purple"
              onClick={requestAccess}
              isLoading={requesting}
              loadingText="Submitting..."
            >
              Request access
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Returns a Chakra Alert matching the current access-request state, or
// null when there's nothing to surface (first-time use).
function renderStatusBanner(existing: AccessRequest | null) {
  if (!existing) return null;
  if (existing.status === 'pending') {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <VStack align="flex-start" spacing={0.5}>
          <HStack>
            <Text fontWeight="600" fontSize="sm">Awaiting approval</Text>
            <Badge colorScheme="blue">pending</Badge>
          </HStack>
          <Text fontSize="xs" color="brand.muted">
            Store <Text as="code">{existing.shopDomain}</Text> submitted — the Reach Social team is
            adding it to the Shopify Partner Dashboard (usually within an hour).
            It's safe to close this window — you'll see an "Install" button on the
            Integrations page when it's ready.
          </Text>
        </VStack>
      </Alert>
    );
  }
  if (existing.status === 'ready') {
    return (
      <Alert status="success" borderRadius="md">
        <AlertIcon />
        <VStack align="flex-start" spacing={0.5}>
          <HStack>
            <Text fontWeight="600" fontSize="sm">Approved — ready to install</Text>
            <Badge colorScheme="green">ready</Badge>
          </HStack>
          <Text fontSize="xs" color="brand.muted">
            Store <Text as="code">{existing.shopDomain}</Text> is on the approved list.
            Click Install to grant Reach Social access to your Shopify catalog.
          </Text>
        </VStack>
      </Alert>
    );
  }
  if (existing.status === 'rejected') {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <VStack align="flex-start" spacing={0.5}>
          <Text fontWeight="600" fontSize="sm">Previous request rejected</Text>
          {existing.notes && (
            <Text fontSize="xs" color="brand.muted">Reason: {existing.notes}</Text>
          )}
          <Text fontSize="xs" color="brand.muted">
            Enter a different store slug below to retry.
          </Text>
        </VStack>
      </Alert>
    );
  }
  if (existing.status === 'installed') {
    return (
      <Alert status="success" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          Already connected to <Text as="code">{existing.shopDomain}</Text>. To
          connect a different store, enter its slug below.
        </Text>
      </Alert>
    );
  }
  return null;
}
